import { FC } from 'react';
import { MAX_RADIUS_MI } from './context';
import { FilterState } from './types';

/**
 * Removable chips summarizing every active filter (README §Filters rail:
 * "Active filters also render as removable chips"). Rendered inside
 * FiltersRail (below the rail controls) since this lane owns both — every
 * chip's remove action patches FilterState the same way its source control
 * would, so it stays a thin derived view rather than owning state.
 */

export interface ActiveFilterChipsProps {
  filters: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
}

interface Chip {
  key: string;
  label: string;
  onRemove: () => void;
}

const WRAP_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 16,
  paddingTop: 16,
  borderTop: '1px solid #E2E5EA',
};

const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#F6F7F9',
  border: '1px solid #E2E5EA',
  color: '#1F2937',
  fontSize: 12,
  fontWeight: 500,
  padding: '3px 8px',
  borderRadius: 999,
};

const REMOVE_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#667085',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};

const ActiveFilterChips: FC<ActiveFilterChipsProps> = ({ filters, onChange }) => {
  const chips: Chip[] = [];

  if (filters.minYears > 0) {
    chips.push({
      key: 'minYears',
      label: `${filters.minYears}+ yrs experience`,
      onRemove: () => onChange({ minYears: 0 }),
    });
  }

  filters.skills.forEach((skill) => {
    chips.push({
      key: `skill-${skill}`,
      label: skill,
      onRemove: () => onChange({ skills: filters.skills.filter((s) => s !== skill) }),
    });
  });

  if (filters.radiusMi < MAX_RADIUS_MI && filters.lat !== null && filters.lng !== null) {
    const center = filters.geoCenterLabel || 'job location';
    chips.push({
      key: 'radius',
      label: `Within ${filters.radiusMi} mi of ${center}`,
      // radiusCustom: removing the chip is a deliberate "Any distance" — it
      // must survive re-selecting the same job's 20mi default (kan-78 item 1).
      onRemove: () => onChange({ radiusMi: MAX_RADIUS_MI, radiusCustom: true }),
    });
  }

  if (!chips.length) return null;

  return (
    <div style={WRAP_STYLE}>
      {chips.map((chip) => (
        <span key={chip.key} style={CHIP_STYLE}>
          {chip.label}
          <button
            type="button"
            style={REMOVE_STYLE}
            onClick={chip.onRemove}
            aria-label={`Remove filter ${chip.label}`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

export default ActiveFilterChips;
